import { FieldValues, Path, useController } from "react-hook-form";
import { Input, FormControl, FormErrorMessage, InputProps } from "@chakra-ui/react";
import { Box } from "@familyzone/component-library";
import { ConfigFormLabel } from "./ConfigFormLabel";
import React from "react";
import { FieldProps } from "./Common";

type Props<TFieldValue extends FieldValues, TFieldPath extends Path<TFieldValue> = Path<TFieldValue>> = {
  fieldProps: FieldProps<TFieldValue, TFieldPath>;
  inputProps?: InputProps;
  styleType?: "row" | "column" | "row-wide";
  padding?: string;
};

export const TextFieldControl = <TFieldValue extends FieldValues, TFieldPath extends Path<TFieldValue> = Path<TFieldValue>>({
  fieldProps,
  inputProps,
  styleType,
  padding,
}: Props<TFieldValue, TFieldPath>): JSX.Element => {
  const { name, control, errors, rules, label, tooltip, testId, onBlur, onChange, disabled } = fieldProps;
  const { field } = useController({
    name,
    control,
    rules: rules,
  });

  return (
    <FormControl
      isInvalid={!!errors[name]}
      sx={{
        display: "flex",
        flexDirection: styleType === "column" ? "column" : "row",
        minHeight: "40px",
        fontSize: "14px",
        alignItems: styleType === "column" ? "left" : "center",
        gap: styleType === "column" ? "4px" : "16px",
        padding: padding ? padding : "0px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: styleType === "column" ? "100%" : styleType === "row-wide" ? "160px" : "120px",
        }}
      >
        {!!label && <ConfigFormLabel label={label} tooltip={tooltip} />}
      </Box>
      <Box flex={1}>
        <Input
          data-testid={!!testId ? testId : `${name}-input`}
          sx={{
            padding: "12px",
            alignContent: "center",
          }}
          // React hook form.
          disabled={disabled}
          value={field.value}
          onChange={(e) => {
            if (disabled) return;
            field.onChange(e);
            if (onChange !== undefined) {
              onChange(e);
            }
          }}
          onBlur={(e) => {
            field.onBlur();
            if (onBlur !== undefined) {
              onBlur(e);
            }
          }}
          name={field.name}
          ref={field.ref}
          // Forward the checkbox props.
          {...inputProps}
        />
        <FormErrorMessage>{errors[name] && errors[name]?.message}</FormErrorMessage>
      </Box>
    </FormControl>
  );
};
